import React, { useEffect, useMemo } from 'react';
import { useLocation } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';

import { actions as filterActions } from 'containers/stats-filter/module';
import { createTeamHomeLink } from 'pages/game-center/game-center-links';
import {
    isProspectMode,
    userHavePlayerIdAndFeatureMask as userHavePlayerIdAndFeatureMaskSelector
} from 'selectors/user/user-features-selector';
import { isUserClient as isUserClientSelector } from 'selectors/user/user-roles-selector';
import { hasAuthKey } from 'utils/api/auth-utils';
import * as endpoints from 'utils/api/endpoints';
import { httpMethods, useFetchData } from 'utils/hooks/use-fetch-data';
import * as expressions from 'utils/helpers/expression-utils';
import { containedIn } from 'utils/helpers/expression-utils';
import { TEAMS_CLIENTS_RECORD_SIZE } from 'widgets/favorites/constant';

import { getBasePath, possession, secondNavBar, shooting, specialTeams } from './helpers';
import { SubLinks, TabBar } from './components';

const SubNavBar = () => {
    const { pathname } = useLocation();

    const statsFilter = useSelector(state => state.statsFilter);

    const seasons = useSelector(state => state.entities.seasons);
    const storedStates = useSelector(state => state.statsFilter.storedStates);
    const activeUser = useSelector(state => state.user);
    const prospectExchangeAccount = useSelector(isProspectMode);
    const isUserClient = useSelector(isUserClientSelector);
    const userHaveSomeFeatureAndPlayerId = useSelector(userHavePlayerIdAndFeatureMaskSelector);

    const dispatch = useDispatch();

    const teamId = useMemo(() => {
        if (!pathname) {
            return null;
        }

        const splitPath = pathname.split('/');

        if (pathname.includes('/premium/team/')) {
            return splitPath[splitPath.length - 1] || null;
        }

        return statsFilter?.teamIds?.[0] || statsFilter?.storedStates?.teamIds?.base?.teamIds?.[0] || null;
    }, [statsFilter, pathname]);

    const subMenuSubLinks = useMemo(() => {
        if (!pathname) return [];

        const basePath = getBasePath();
        const splitPath = pathname.split('/');

        if (pathname.includes('/videos') || pathname.includes('/skills')) {
            const id = splitPath[splitPath.length - 2];

            return [
                { menu: 'Video', path: `/scout/players/${id}/videos` },
                { menu: 'Skills', path: `/scout/players/${id}/skills` }
            ];
        }

        if (pathname.includes('player-comparison')) {
            return [];
        }

        if (
            (pathname.includes('/premium/team/') ||
                secondNavBar(basePath, teamId).some(
                    ({ path, subPath }) =>
                        subPath?.some(({ path: sub }) => pathname.includes(sub)) || pathname.includes(path)
                )) &&
            pathname !== createTeamHomeLink(basePath).to
        ) {
            return secondNavBar(basePath, teamId);
        }

        return [];
    }, [pathname, teamId]);

    const tabBarLinks = useMemo(() => {
        const basePath = getBasePath();
        const sections = [specialTeams(basePath), shooting(basePath), possession(basePath)];

        return sections.find(section => section.some(({ path }) => pathname.includes(path))) || [];
    }, [pathname]);

    const isDisabledSubLinks = useMemo(() => {
        const showProfile = hasAuthKey() && !!activeUser && !activeUser.isProcessing;
        const isVisibleTopSearch = isUserClient && !userHaveSomeFeatureAndPlayerId;
        const isHiddenPath = pathname.includes('premium/home') || pathname.includes('players-home');

        return isHiddenPath || prospectExchangeAccount || !showProfile || !isVisibleTopSearch;
    }, [pathname, prospectExchangeAccount, activeUser, isUserClient, userHaveSomeFeatureAndPlayerId]);

    const isDisabledTabBar = useMemo(() => {
        const showProfile = hasAuthKey() && !!activeUser && !activeUser.isProcessing;
        const isVisibleTopSearch = isUserClient && !userHaveSomeFeatureAndPlayerId;

        return !showProfile || !isVisibleTopSearch;
    }, [activeUser, isUserClient, userHaveSomeFeatureAndPlayerId]);

    const opponentsExpression = useMemo(
        () =>
            expressions.and(
                expressions.or(
                    teamId ? containedIn('homeTeamId', ...[expressions.number(teamId)]) : null,
                    teamId ? containedIn('awayTeamId', ...[expressions.number(teamId)]) : null
                )
            ),
        [teamId]
    );

    const { data: gamesData } = useFetchData({
        method: httpMethods.post,
        url: endpoints.teamsClientGames,
        payload: {
            size: TEAMS_CLIENTS_RECORD_SIZE,
            page: 0,
            orders: [{ direction: 'DESC', property: 'date' }, { direction: 'DESC', property: 'dateTime' }],
            expression: opponentsExpression
        },
        initialCondition: !!teamId && !storedStates.teamIds
    });

    useEffect(() => {
        if (!gamesData || !teamId || !seasons || !!storedStates?.teamIds) {
            return;
        }

        const lastGame = gamesData?.[0];

        if (!lastGame) {
            return;
        }

        const { id: gameId, seasonId } = lastGame;

        const leagueId = seasons[seasonId]?.leagueId;

        const filterData = {
            gameIds: {
                base: {
                    gameIds: [Number(gameId)],
                    ignoredGameIds: []
                }
            },
            teamIds: {
                base: {
                    teamIds: [Number(teamId)]
                }
            },
            seasonIds: {
                base: {
                    seasonIds: [Number(seasonId)]
                }
            },
            leagueIds: {
                base: {
                    leagueIds: [Number(leagueId)]
                }
            }
        };

        dispatch(filterActions.updateStoredStates(filterData));
    }, [gamesData, teamId, seasons, storedStates?.teamIds, dispatch]);

    return (
        <>
            <SubLinks isDisabled={isDisabledSubLinks} links={subMenuSubLinks} />
            <TabBar links={tabBarLinks} isDisabled={isDisabledTabBar} />
        </>
    );
};

export default SubNavBar;
